// Reusable dropdown menu component
// Provides a clean kebab menu (three dots) for actions
// CHANGE: Implemented React Portal for menu rendering at body level
// CHANGE: Added dynamic repositioning on window resize to prevent clipping
// FIXED: Moved early return after all hooks to comply with Rules of Hooks

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { getPortalRoot } from '../../lib/portalUtils';

export interface DropdownMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

interface DropdownMenuProps {
  items: DropdownMenuItem[];
  align?: 'left' | 'right';
}

interface MenuPosition {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}

export function DropdownMenu({ items, align = 'right' }: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Calculate menu position based on trigger button position
  const calculatePosition = () => {
    if (!triggerRef.current || !menuRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const position: MenuPosition = {};

    // Vertical positioning
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const menuHeight = menuRect.height || 200; // Fallback height

    if (spaceBelow >= menuHeight || spaceBelow > spaceAbove) {
      // Position below
      position.top = triggerRect.bottom + 4;
    } else {
      // Position above
      position.bottom = viewportHeight - triggerRect.top + 4;
    }

    // Horizontal positioning
    const menuWidth = menuRect.width || 180; // Fallback width

    if (align === 'right') {
      // Try to align right edge of menu with right edge of trigger
      const rightAlignLeft = triggerRect.right - menuWidth;
      if (rightAlignLeft >= 0) {
        position.left = rightAlignLeft;
      } else {
        // Not enough space, align to left edge of viewport with padding
        position.left = 8;
      }
    } else {
      // Try to align left edge of menu with left edge of trigger
      const leftAlignRight = triggerRect.left + menuWidth;
      if (leftAlignRight <= viewportWidth) {
        position.left = triggerRect.left;
      } else {
        // Not enough space, align to right edge of viewport with padding
        position.right = 8;
      }
    }

    setMenuPosition(position);
  };

  // Update position when menu opens
  useEffect(() => {
    if (isOpen) {
      calculatePosition();
    }
  }, [isOpen]);

  // Recalculate position on window resize
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      calculatePosition();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, align]);

  // Handle click outside, escape key, and scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        menuRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  if (items.length === 0) {
    return null;
  }

  const handleItemClick = (item: DropdownMenuItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  // Render menu items in a portal
  const renderMenu = () => {
    if (!isOpen) return null;

    return createPortal(
      <div
        ref={menuRef}
        className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[180px] z-[9999]"
        style={{
          top: menuPosition.top !== undefined ? `${menuPosition.top}px` : undefined,
          bottom: menuPosition.bottom !== undefined ? `${menuPosition.bottom}px` : undefined,
          left: menuPosition.left !== undefined ? `${menuPosition.left}px` : undefined,
          right: menuPosition.right !== undefined ? `${menuPosition.right}px` : undefined,
        }}
      >
        {items.map((item, index) => (
          <button
            key={index}
            onClick={() => handleItemClick(item)}
            disabled={item.disabled}
            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-left transition-colors duration-200 ${
              item.variant === 'danger'
                ? 'text-red-700 hover:bg-red-50'
                : 'text-gray-700 hover:bg-gray-50'
            } ${
              item.disabled
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer'
            }`}
          >
            {item.icon && (
              <span className="flex-shrink-0">{item.icon}</span>
            )}
            <span>{item.label}</span>
          </button>
        ))}
      </div>,
      getPortalRoot()
    );
  };

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
        aria-label="Actions"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <MoreVertical size={20} className="text-gray-600" />
      </button>
      {renderMenu()}
    </>
  );
}
